import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import ListFieldMobileLabel from '#/modules/commonComponents/form/ListFieldMobileLabel';
import FloatingSelect from '#/modules/commonComponents/form/select';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import { Button, DESTRUCTIVE_GHOST_BUTTON_CLASS } from '#/modules/commonComponents/button';

/** 值是否必填｜規格｜移除；必填欄固定約 5rem */
const LINE_GRID_COLS = '@3xl/form-section:grid-cols-[4rem_minmax(0,1fr)_auto]';

const LINE_HEADER_CLASS = cn(
  'sticky top-0 z-[var(--z-content-sticky)]',
  'hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@3xl/form-section:grid',
  LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full grid-cols-1 gap-2 px-3 py-2.5',
  '@3xl/form-section:items-center',
  LINE_GRID_COLS,
);

const SPECS_LIST_SCROLL_STYLE = {
  '--form-section-scroll-max-height':
    'min(32rem, calc(100vh - var(--form-list-chrome)))',
};

const MtrTemplateSpecsEditor = ({
  control,
  errors,
  specOptions,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'specs',
  });

  const watchedSpecs = useWatch({
    control,
    name: 'specs',
    defaultValue: [],
  });

  const usedSpecIds = useMemo(
    () => new Set((watchedSpecs ?? []).map((rule) => rule?.mtrSpecId).filter(Boolean)),
    [watchedSpecs],
  );

  function handleAddRule() {
    append({
      mtrSpecId: '',
      required: false,
      displayOrder: fields.length,
    });
  }

  return (
    <div className="form-section @container/form-section w-full">
      <div className="form-section__header">
        <h2 className="form-block__title">規格規則</h2>
        <Button type="button" size="sm" onClick={handleAddRule}>
          新增規則
        </Button>
      </div>

      {fields.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">尚未新增規格規則，請至少新增一筆。</p>
      ) : null}

      {errors?.specs?.message ? (
        <p className="mt-3 text-sm text-red-500">{errors.specs.message}</p>
      ) : null}

      {fields.length > 0 ? (
        <div
          className="form-section__scroll"
          style={SPECS_LIST_SCROLL_STYLE}
        >
          <div className={LINE_HEADER_CLASS} aria-hidden>
            <p>值是否必填</p>
            <p>規格</p>
            <p className="justify-self-end text-right">移除</p>
          </div>
          <ul className="m-0 list-none divide-y divide-gray-200 p-0">
            {fields.map((field, idx) => {
              const currentValue = watchedSpecs?.[idx]?.mtrSpecId;
              const rowOptions = specOptions.filter(
                (option) => option.value === currentValue || !usedSpecIds.has(option.value),
              );

              return (
                <li key={field.id} className={LINE_ROW_CLASS}>
                  <div className="min-w-0">
                    <ListFieldMobileLabel>值是否必填</ListFieldMobileLabel>
                    <Controller
                      name={`specs.${idx}.required`}
                      control={control}
                      render={({ field: checkboxField }) => (
                        <label className="flex justify-center items-center gap-2 text-sm text-gray-700">
                          <Checkbox
                            checked={Boolean(checkboxField.value)}
                            onCheckedChange={checkboxField.onChange}
                            aria-label="值是否必填"
                          />
                        </label>
                      )}
                    />
                  </div>
                  <div className="min-w-0">
                    <ListFieldMobileLabel>規格</ListFieldMobileLabel>
                    <FloatingSelect
                      id={`specs.${idx}.mtrSpecId`}
                      options={rowOptions}
                      control={control}
                      errors={errors}
                      placeholder="請選擇規格"
                      containerClassName="w-full"
                    />
                  </div>
                  <div className="flex min-w-0 items-center justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                      aria-label="移除規格規則"
                      onClick={() => remove(idx)}
                    >
                      <PiTrashBold className="size-4" aria-hidden />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

MtrTemplateSpecsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func,
  errors: PropTypes.object,
  specOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default MtrTemplateSpecsEditor;
